import moment from 'moment';
import { createReportPdf, chartToBase64 } from '@/utils/pdfReport';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from '@/plugins/AccessDenied.vue';
import FeatureNotAvailable from '@/plugins/FeatureNotAvailable.vue';
import DateRangePicker from 'vue3-daterange-picker';

export default {
  components: { PlaceholderContent, AccessDenied, FeatureNotAvailable, DateRangePicker },
  data() {
    const today = new Date();
    const weekAgo = new Date(Date.now() - 6 * 86400000);
    const iso = d => d.toISOString().slice(0, 10);
    return {
      pdfLoading: false,
      pageLoading: true,
      hasPermission: true,
      // แพ็กของร้านไม่มีรายงานนี้ — คนละเรื่องกับ hasPermission (ดู loadReport)
      featureUnavailable: false,
      loading: false,
      errorMessage: '',
      branches: [],
      filters: {
        branch: 'all',
        date_range: { startDate: weekAgo, endDate: today },
        // legacy keys — kept for report pages not yet migrated to date_range
        dateFrom: iso(weekAgo),
        dateTo: iso(today),
      },
    };
  },
  computed: {
    selectedBranchLabel() {
      if (this.filters.branch === 'all') return this.$t('All Branches');
      return this.branches.find(b => b.id === this.filters.branch)?.name || '';
    },
    // Quick-select presets for the date-range picker. Keys are translated
    // labels; values are [start, end] Date pairs the picker applies on click.
    dateRanges() {
      const endToday = moment().endOf('day').toDate();
      return {
        [this.$t('Today')]: [moment().startOf('day').toDate(), endToday],
        [this.$t('This week')]: [moment().startOf('isoWeek').toDate(), endToday],
        [this.$t('This month')]: [moment().startOf('month').toDate(), endToday],
        [this.$t('This year')]: [moment().startOf('year').toDate(), endToday],
        [this.$t('Last year')]: [
          moment().subtract(1, 'year').startOf('year').toDate(),
          moment().subtract(1, 'year').endOf('year').toDate(),
        ],
      };
    },
    dateRangeLocale() {
      return {
        firstDay: 1,
        format: 'yyyy-mm-dd',
        applyLabel: this.$t('Apply'),
        cancelLabel: this.$t('Cancel'),
        clearable: false,
        monthNames: this.$storeIndex.monthNames(),
        daysOfWeek: this.$storeIndex.daysOfWeek(),
      };
    },
  },
  watch: {
    'filters.branch'() {
      this.loadReport();
    },
    'filters.date_range': {
      deep: true,
      handler(val, old) {
        const a = (val && val.startDate) ? String(val.startDate) : '';
        const b = (val && val.endDate) ? String(val.endDate) : '';
        const oa = (old && old.startDate) ? String(old.startDate) : '';
        const ob = (old && old.endDate) ? String(old.endDate) : '';
        if (a === oa && b === ob) return;
        this.loadReport();
      },
    },
  },
  methods: {
    formatDate(d) {
      return d ? moment(d).format('YYYY-MM-DD') : '';
    },
    formatCurrency(v) {
      return new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0 }).format(v || 0);
    },
    formatCurrencyRaw(v) {
      return new Intl.NumberFormat('th-TH', { minimumFractionDigits: 0 }).format(v || 0);
    },
    formatCompact(v) {
      return v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v || 0);
    },

    /**
     * Build the params object passed to a report endpoint. Reads the active
     * date_range and maps it to the API's start/end (YYYY-MM-DD).
     */
    buildFetchParams() {
      return {
        branch_id: this.filters.branch,
        start: this.formatDate(this.filters.date_range.startDate),
        end: this.formatDate(this.filters.date_range.endDate),
      };
    },

    /**
     * Wraps the page's fetchReport() with loading/permission/error state.
     * First call shows the skeleton (pageLoading); later calls show a soft
     * overlay (loading) so the user keeps seeing previous data.
     */
    async loadReport() {
      if (typeof this.fetchReport !== 'function') return;
      const isInitial = !this._reportLoadedOnce;
      if (isInitial) this.pageLoading = true;
      else this.loading = true;
      this.errorMessage = '';
      try {
        await this.fetchReport();
        this.hasPermission = true;
        this._reportLoadedOnce = true;
      } catch (err) {
        const status = err?.response?.status;
        const code = err?.response?.data?.code;
        // 403 มาได้ 2 สาเหตุ ต้องแยกให้ผู้ใช้เข้าใจ (../silver-spoon-api/docs/report-catalog.md)
        //   ERROR_FEATURE_NOT_AVAILABLE = แพ็กของร้านไม่มีรายงานนี้ → <feature-not-available /> (ติดต่ออัปเกรดแพ็ก)
        //   นอกนั้น                      = มีรายงาน แต่พนักงานคนนี้ไม่มีสิทธิ์ดู → <access-denied />
        // ERROR_REPORT_NOT_AVAILABLE = code เดิมก่อนระบบแพ็ก — รับไว้ช่วง FE กับ API deploy ไม่พร้อมกัน
        if (status === 403 && (code === 'ERROR_FEATURE_NOT_AVAILABLE' || code === 'ERROR_REPORT_NOT_AVAILABLE')) {
          this.featureUnavailable = true;
        } else if (status === 403) {
          this.hasPermission = false;
        } else {
          this.errorMessage = err?.response?.data?.message || err?.message || this.$t('Failed to load data');
        }
      } finally {
        this.pageLoading = false;
        this.loading = false;
      }
    },

    // Retained so report pages not yet migrated off the Apply button keep working.
    applyFilters() {
      this.loadReport();
    },

    buildPdfHeader() {
      const branchLabel = this.selectedBranchLabel;
      const period = `${this.formatDate(this.filters.date_range.startDate)} — ${this.formatDate(this.filters.date_range.endDate)}`;
      return [
        { text: `Siva — ${this.$t(this.reportLabel)}`, style: 'reportTitle' },
        { text: `${branchLabel}  |  ${period}`, style: 'reportSubtitle' },
      ];
    },

    async getChartImage(refName) {
      const ref = this.$refs[refName];
      if (!ref) return null;
      return chartToBase64(ref);
    },

    async previewPdf() {
      this.pdfLoading = true;
      try {
        const docDef = await this.buildPdfDefinition();
        const pdf = createReportPdf(docDef);
        const blob = await pdf.getBlob();
        const url = URL.createObjectURL(blob);
        let iframe = document.getElementById('__pdf_print_frame');
        if (iframe) iframe.remove();
        iframe = document.createElement('iframe');
        iframe.id = '__pdf_print_frame';
        iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
        iframe.src = url;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
          URL.revokeObjectURL(url);
        };
      } catch (e) {
        console.error('PDF generation error:', e);
        this.$toast.error('PDF generation failed: ' + (e.message || e));
      } finally {
        this.pdfLoading = false;
      }
    },

    async downloadPdf() {
      this.pdfLoading = true;
      try {
        const docDef = await this.buildPdfDefinition();
        const pdf = createReportPdf(docDef);
        const from = this.formatDate(this.filters.date_range.startDate);
        const to = this.formatDate(this.filters.date_range.endDate);
        pdf.download(`report-${this.$route.name}-${from}-${to}.pdf`);
      } catch (e) {
        console.error('PDF download error:', e);
        this.$toast.error('Failed to download PDF');
      } finally {
        this.pdfLoading = false;
      }
    },
  },
  mounted() {
    this.loadReport();
  },
};
